import { Dispatch, SetStateAction, useState } from 'react';
import { z } from 'zod';

export type ResetFunction = () => void;

/**
 * Goal of this type: Ensure that the data is not a function. Otherwise `setDataWrapper` gets angry because it can't
 * be sure `newState` is callable (e.g. change `S extends z.ZodType<AnyRecords>` to `S extends z.ZodTypeAny`). This
 * is just one way to solve the problem. There may be other, better ways.
 **/
type AnyRecords = Record<any, any> | Record<any, any>[];

type UseLocalPersistedStateProps<DeserializedData extends AnyRecords, Schema extends z.ZodType<DeserializedData>> = {
  dataSchema: Schema;
  initialData: DeserializedData;
  /**
   * If not specified then the data will not be persisted and this hook acts like `useState`.
   * Optional so that consumers can allow for optional functionality as well.
   **/
  persistenceKey?: string | null;
  useSessionStorage?: boolean;
};

type UseLocalPersistedStateResult<DeserializedData extends AnyRecords> = [
  DeserializedData,
  Dispatch<SetStateAction<DeserializedData>>,
  ResetFunction,
];

export const useLocalPersistedState = <
  DeserializedData extends AnyRecords,
  Schema extends z.ZodType<DeserializedData>,
>({
  dataSchema,
  initialData,
  persistenceKey,
  useSessionStorage = false,
}: UseLocalPersistedStateProps<DeserializedData, Schema>): UseLocalPersistedStateResult<DeserializedData> => {
  const localStorageKey = persistenceKey ? persistenceKey : null;
  const storage = useSessionStorage ? window.sessionStorage : window.localStorage;

  const loadData = (): DeserializedData => {
    if (!localStorageKey) {
      return initialData;
    }

    const serializedData = storage.getItem(localStorageKey);
    if (!serializedData) {
      return initialData;
    }

    const deserializedData = JSON.parse(serializedData);
    const parsedData = dataSchema.safeParse(deserializedData);

    if (!parsedData.success) {
      // Since we can't parse the data, clear it from storage
      storage.removeItem(localStorageKey);
      return initialData;
    }

    return parsedData.data;
  };

  const saveData = (data: DeserializedData): void => {
    if (!localStorageKey) {
      return;
    }

    storage.setItem(localStorageKey, JSON.stringify(data));
  };

  const [data, setData] = useState<DeserializedData>(loadData());

  const setDataWrapper = (newState: SetStateAction<DeserializedData>): void => {
    const newData = typeof newState === 'function' ? newState(data) : newState;

    setData(newState);
    saveData(newData);
  };

  const resetData = (): void => {
    setDataWrapper(initialData);
  };

  return [data, setDataWrapper, resetData];
};
